import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ModalCard from '../ui/ModalCard';
import TextInput from '../ui/TextInput';

interface ForgotPasswordFormCardProps {
  initialEmail: string;
  onSent: (email: string) => void;
}

export default function ForgotPasswordFormCard({ initialEmail, onSent }: ForgotPasswordFormCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState(initialEmail);
  // Per requirements this confirmation checkbox is unchecked by default,
  // even though the Figma design shows it checked.
  const [notRobot, setNotRobot] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = email.trim() !== '' && notRobot && !submitting;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);

    // TODO(backend): send the password-reset request, e.g.
    //   await authApi.requestPasswordReset({ email });

    onSent(email);
    setSubmitting(false);
  };

  return (
    <ModalCard onSubmit={handleSubmit}>
      <Typography variant="h2" component="h1">
        {t('forgotPassword.headline')}
        <Box component="span" sx={(theme) => ({ color: theme.app.accent })}>
          {t('forgotPassword.headlineAction')}
        </Box>
      </Typography>

      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('forgotPassword.description')}
      </Typography>

      <TextInput
        id="forgot-email"
        type="email"
        autoComplete="email"
        label={t('forgotPassword.emailLabel')}
        placeholder={t('forgotPassword.emailPlaceholder')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />

      <FormControlLabel
        sx={{ alignItems: 'center', m: 0, gap: 1.5 }}
        control={
          <Checkbox
            checked={notRobot}
            onChange={(e) => setNotRobot(e.target.checked)}
            sx={{ p: 0, color: 'text.secondary' }}
          />
        }
        label={
          <Typography variant="body2" sx={(theme) => ({ color: theme.app.foreground })}>
            {t('forgotPassword.checkboxLabel')}
          </Typography>
        }
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={!canSubmit}
          sx={(theme) => ({
            backgroundColor: theme.app.primary,
            color: theme.app.primaryForeground,
            '&:hover': { backgroundColor: theme.app.primaryHover },
          })}
        >
          {t('forgotPassword.submit')}
        </Button>
        <Button
          type="button"
          variant="outlined"
          fullWidth
          onClick={() => navigate('/login')}
          sx={(theme) => ({
            color: theme.app.foreground,
            borderColor: theme.app.borderStrong,
            '&:hover': {
              borderColor: theme.app.borderStrong,
              backgroundColor: theme.app.secondary,
            },
          })}
        >
          {t('forgotPassword.backToLogin')}
        </Button>
      </Box>
    </ModalCard>
  );
}
